import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { dashboardStyles } from '../../styles/dashboard-styles.js';
import { authService } from '../../services/auth-service.js';
import { liveCallsService } from '../../services/live-calls-service.js';
import { tz } from '../../services/timezone-service.js';

const API_BASE = '/voice/php/api';

export class ModernDashboard extends LitElement {
  static properties = {
    _stats: { state: true },
    _activeCalls: { state: true },
    _hourlyData: { state: true },
    _chartData: { state: true },
    _chartLabels: { state: true },
    _chartRange: { state: true },
    _chartLoading: { state: true },
    _activityFeed: { state: true },
    _insights: { state: true },
    _performanceMetrics: { state: true },
    _trends: { state: true },
    _loading: { state: true },
    _error: { state: true },
    _now: { state: true },
    _liveConnected: { state: true }
  };

  static styles = [designSystem, componentStyles, dashboardStyles];

  constructor() {
    super();
    this._stats = { activeCalls: 0, totalToday: 0, avgDuration: '0:00', successRate: 0 };
    this._activeCalls = [];
    this._hourlyData = Array(24).fill(0);
    this._chartData = Array(24).fill(0);
    this._chartLabels = [];
    this._chartRange = '24h';
    this._chartLoading = false;
    this._activityFeed = [];
    this._insights = [];
    this._performanceMetrics = [];
    this._trends = {
      activeCalls: { value: 0, direction: 'up' },
      totalToday: { value: 0, direction: 'up' },
      avgDuration: { value: 0, direction: 'down' },
      successRate: { value: 0, direction: 'up' }
    };
    this._loading = true;
    this._error = null;
    this._now = Date.now();
    this._liveConnected = false;
    this._durationTimer = null;
    this._liveCallsUnsub = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this._loadDashboard();

    // Subscribe to live calls for real-time active call updates
    this._liveCallsUnsub = liveCallsService.onChange((calls, connected) => {
      this._activeCalls = calls;
      this._stats = { ...this._stats, activeCalls: calls.length };
      this._liveConnected = connected;
    });

    // Seed with current live data immediately
    const currentCalls = liveCallsService.getActiveCalls();
    if (currentCalls.length) {
      this._activeCalls = currentCalls;
      this._stats = { ...this._stats, activeCalls: currentCalls.length };
    }
    this._liveConnected = liveCallsService.isConnected();

    // Tick every second for live duration display
    this._durationTimer = setInterval(() => {
      this._now = Date.now();
    }, 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._liveCallsUnsub) {
      this._liveCallsUnsub();
      this._liveCallsUnsub = null;
    }
    if (this._durationTimer) {
      clearInterval(this._durationTimer);
      this._durationTimer = null;
    }
  }

  async _loadDashboard() {
    this._loading = true;
    this._error = null;

    try {
      const res = await authService.apiFetch(`${API_BASE}/dashboard.php?ujid=1&chart_range=${this._chartRange}`);

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const data = await res.json();

      if (!data.success) {
        throw new Error(data.error || 'API returned failure');
      }

      // Use API stats but override activeCalls count with live data
      const liveCount = liveCallsService.getActiveCallsCount();
      this._stats = { ...data.stats, activeCalls: liveCount || data.stats.activeCalls };
      // Active calls come from liveCallsService, not the API snapshot
      const liveCalls = liveCallsService.getActiveCalls();
      if (liveCalls.length || liveCallsService.isConnected()) {
        this._activeCalls = liveCalls;
      } else {
        this._activeCalls = data.activeCalls;
      }
      this._hourlyData = data.hourlyData;
      this._chartData = data.chartData || data.hourlyData;
      this._chartLabels = data.chartLabels || [];
      this._activityFeed = data.activityFeed;
      this._insights = data.insights;
      this._performanceMetrics = data.performanceMetrics;
      this._trends = data.trends;

    } catch (err) {
      console.error('[Dashboard] Load error:', err);
      this._error = err.message || 'Failed to load dashboard';
    } finally {
      this._loading = false;
    }
  }

  async _switchChartRange(range) {
    if (range === this._chartRange) return;
    this._chartRange = range;
    this._chartLoading = true;
    try {
      const res = await authService.apiFetch(`${API_BASE}/dashboard.php?ujid=1&chart_range=${range}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'API error');
      this._chartData = data.chartData || data.hourlyData;
      this._chartLabels = data.chartLabels || [];
    } catch (err) {
      console.error('[Dashboard] Chart range error:', err);
    } finally {
      this._chartLoading = false;
    }
  }

  _handleRefresh() {
    this._loadDashboard();
  }

  render() {
    const currentHour = parseInt(new Date().toLocaleTimeString('en-US', { timeZone: tz.tz, hour12: false, hour: '2-digit' }), 10);
    const chartData = this._chartData || this._hourlyData;
    const maxVal = Math.max(...chartData, 1);

    return html`
      ${iconStyles}
      <div class="dashboard-header">
        <div>
          <div class="dashboard-title">Dashboard Overview</div>
          <div class="dashboard-subtitle">Real-time performance and call analytics</div>
        </div>
        <div class="header-actions">
          <div class="live-indicator">
            <span class="live-dot"></span>
            System Live
          </div>
          <button class="btn btn-secondary btn-sm" @click=${() => this._handleRefresh()} ?disabled=${this._loading}>
            <i class="fa-sharp fa-light fa-arrow-rotate-right${this._loading ? ' fa-spin' : ''}"></i>
            ${this._loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      ${this._loading && !this._stats.totalToday ? html`
        <div style="display: flex; align-items: center; justify-content: center; min-height: 400px;">
          <div style="text-align: center;">
            <i class="fa-sharp fa-light fa-spinner-third fa-spin" style="font-size: 2rem; color: var(--color-text-tertiary);"></i>
            <div style="margin-top: var(--space-4); font-family: var(--font-body); color: var(--color-text-tertiary);">
              Loading dashboard data...
            </div>
          </div>
        </div>
      ` : html`

      ${this._error ? html`
        <div style="background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.3); border-radius: var(--radius-lg); padding: var(--space-3) var(--space-4); margin-bottom: var(--space-4); display: flex; align-items: center; gap: var(--space-3); font-family: var(--font-body); font-size: var(--text-sm); color: #ef4444;">
          <i class="fa-sharp fa-light fa-triangle-exclamation"></i>
          <span>Failed to load dashboard: ${this._error}</span>
          <button class="btn btn-sm btn-secondary" @click=${() => this._handleRefresh()} style="margin-left: auto;">Retry</button>
        </div>
      ` : ''}

      <div class="dashboard-grid">
        <!-- KPI Cards -->
        <div class="kpi-row">
          <!-- Active Calls -->
          <div class="kpi-card">
            <div class="kpi-top">
              <div class="kpi-icon">
                <i class="fa-sharp-duotone fa-solid fa-phone-arrow-up-right"></i>
              </div>
              <span class="kpi-trend ${this._trends.activeCalls.direction === 'up' ? 'positive' : 'negative'}">
                <i class="fa-sharp fa-light fa-arrow-${this._trends.activeCalls.direction}" style="font-size: 10px;"></i>
                ${this._trends.activeCalls.direction === 'up' ? '+' : '-'}${this._trends.activeCalls.value}%
              </span>
            </div>
            <div class="kpi-value-row">
              <span class="kpi-value">${this._stats.activeCalls}</span>
              <span class="kpi-pulse-dot"></span>
            </div>
            <div class="kpi-label">Active Calls</div>
          </div>

          <!-- Total Calls Today -->
          <div class="kpi-card">
            <div class="kpi-top">
              <div class="kpi-icon">
                <i class="fa-sharp-duotone fa-solid fa-chart-line-up"></i>
              </div>
              <span class="kpi-trend ${this._trends.totalToday.direction === 'up' ? 'positive' : 'negative'}">
                <i class="fa-sharp fa-light fa-arrow-${this._trends.totalToday.direction}" style="font-size: 10px;"></i>
                ${this._trends.totalToday.direction === 'up' ? '+' : '-'}${this._trends.totalToday.value}%
              </span>
            </div>
            <div class="kpi-value">${this._stats.totalToday}</div>
            <div class="kpi-label">Total Calls Today</div>
          </div>

          <!-- Avg Duration -->
          <div class="kpi-card">
            <div class="kpi-top">
              <div class="kpi-icon">
                <i class="fa-sharp-duotone fa-solid fa-timer"></i>
              </div>
              <span class="kpi-trend ${this._trends.avgDuration.direction === 'up' ? 'positive' : 'negative'}">
                <i class="fa-sharp fa-light fa-arrow-${this._trends.avgDuration.direction}" style="font-size: 10px;"></i>
                ${this._trends.avgDuration.direction === 'up' ? '+' : '-'}${this._trends.avgDuration.value}%
              </span>
            </div>
            <div class="kpi-value">${this._stats.avgDuration}</div>
            <div class="kpi-label">Avg Duration</div>
          </div>

          <!-- Success Rate -->
          <div class="kpi-card">
            <div class="kpi-top">
              <div class="kpi-icon">
                <i class="fa-sharp-duotone fa-solid fa-bullseye-arrow"></i>
              </div>
              <span class="kpi-trend ${this._trends.successRate.direction === 'up' ? 'positive' : 'negative'}">
                <i class="fa-sharp fa-light fa-arrow-${this._trends.successRate.direction}" style="font-size: 10px;"></i>
                ${this._trends.successRate.direction === 'up' ? '+' : '-'}${this._trends.successRate.value}%
              </span>
            </div>
            <div class="kpi-value">${this._stats.successRate}%</div>
            <div class="kpi-label">Success Rate</div>
          </div>
        </div>

        <!-- Chart + Activity Feed -->
        <div class="two-col" style="animation-delay: 120ms;">
          <!-- Call Volume Chart -->
          <div class="panel">
            <div class="panel-header">
              <span class="panel-title">Call Volume</span>
              <div class="chart-controls">
                ${['24h', '7d', '30d'].map(r => html`
                  <button class="btn btn-sm ${this._chartRange === r ? 'btn-primary' : 'btn-ghost'}"
                    @click=${() => this._switchChartRange(r)}
                    ?disabled=${this._chartLoading}
                  >${r.toUpperCase()}</button>
                `)}
              </div>
            </div>
            <div class="panel-body" style="position:relative;">
              ${this._chartLoading ? html`
                <div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(255,255,255,0.5);z-index:2;border-radius:inherit;">
                  <i class="fa-sharp fa-light fa-spinner-third fa-spin" style="font-size:1.25rem;color:var(--color-text-tertiary);"></i>
                </div>
              ` : ''}
              <div class="chart-area">
                ${chartData.map((val, i) => html`
                  <div class="chart-bar-wrapper">
                    <div
                      class="chart-bar ${this._chartRange === '24h' && i === currentHour ? 'current-hour' : ''}"
                      style="height: ${(val / maxVal) * 100}%;"
                    >
                      <span class="chart-tooltip">${val} calls</span>
                    </div>
                  </div>
                `)}
              </div>
              <div class="chart-labels">
                ${(this._chartLabels.length ? this._chartLabels : chartData.map((_, i) =>
                  i % 3 === 0 ? (i === 0 ? '12a' : i < 12 ? `${i}a` : i === 12 ? '12p' : `${i - 12}p`) : ''
                )).map(label => html`
                  <div class="chart-label">${label}</div>
                `)}
              </div>
            </div>
          </div>

          <!-- Activity Feed -->
          <div class="panel">
            <div class="panel-header">
              <span class="panel-title">Activity Feed</span>
              <button class="btn btn-sm btn-ghost">View All</button>
            </div>
            <div class="panel-body">
              <div class="activity-feed">
                ${this._activityFeed.map(item => html`
                  <div class="activity-item">
                    <div class="activity-icon ${item.type}">
                      ${this._getActivityIcon(item.type)}
                    </div>
                    <div class="activity-body">
                      <div class="activity-text">${this._renderHtml(item.text)}</div>
                      <div class="activity-time">${item.time}</div>
                    </div>
                  </div>
                `)}
              </div>
            </div>
          </div>
        </div>

        <!-- Active Calls + AI Insights -->
        <div class="two-col" style="animation-delay: 240ms;">
          <!-- Active Calls -->
          <div class="panel">
            <div class="panel-header">
              <span class="panel-title">Active Calls</span>
              <span class="badge badge-success">
                <span class="status-dot active"></span>
                ${this._activeCalls.length} Live
              </span>
            </div>
            <div class="panel-body">
              <div class="active-calls-list">
                ${this._activeCalls.length > 0
                  ? this._activeCalls.map(call => html`
                    <div class="active-call-row">
                      <div class="call-status-dot"></div>
                      <div class="call-caller">
                        <div class="call-caller-number">${this._formatPhone(call.phone_number || call.phone)}</div>
                        <div class="call-caller-agent">${call.agent_name || call.agent || 'AI Agent'}</div>
                      </div>
                      <div class="call-duration-live">${this._liveDuration(call)}</div>
                      <div class="call-sentiment ${call.sentiment || 'neutral'}" title="${call.sentiment || 'neutral'}"></div>
                    </div>
                  `)
                  : html`
                    <div class="empty-state" style="padding: var(--space-8);">
                      <div class="empty-state-icon"><i class="fa-sharp fa-light fa-phone-xmark"></i></div>
                      <div class="empty-state-title">No Active Calls</div>
                      <div class="empty-state-description">Calls will appear here when they connect</div>
                    </div>
                  `
                }
              </div>
            </div>
          </div>

          <!-- AI Insights -->
          <div class="panel">
            <div class="panel-header">
              <span class="panel-title">AI Insights</span>
              <span class="badge badge-info">
                <i class="fa-sharp fa-light fa-sparkles" style="font-size: 10px;"></i>
                Auto-generated
              </span>
            </div>
            <div class="panel-body">
              <div class="insights-list">
                ${this._insights.map(insight => html`
                  <div class="insight-card">
                    <div class="insight-icon"><i class="${insight.icon}"></i></div>
                    <div class="insight-title">${insight.title}</div>
                    <div class="insight-text">${insight.text}</div>
                  </div>
                `)}
              </div>
            </div>
          </div>
        </div>

        <!-- Today's Performance Bar -->
        ${this._performanceMetrics.length ? html`
          <div class="performance-bar">
            ${this._performanceMetrics.map(m => html`
              <div class="perf-stat">
                <div class="perf-icon"><i class="${m.icon}"></i></div>
                <div class="perf-info">
                  <div class="perf-value">${m.value}</div>
                  <div class="perf-label">${m.label}</div>
                </div>
              </div>
            `)}
          </div>
        ` : ''}
      </div>
      `}
    `;
  }

  _getActivityIcon(type) {
    const icons = {
      'call-in': html`<i class="fa-sharp fa-light fa-phone-arrow-down-left"></i>`,
      'call-out': html`<i class="fa-sharp fa-light fa-phone-arrow-up-right"></i>`,
      'call-end': html`<i class="fa-sharp fa-light fa-phone-xmark"></i>`,
      'transfer': html`<i class="fa-sharp fa-light fa-phone-arrow-right"></i>`,
      'alert': html`<i class="fa-sharp fa-light fa-triangle-exclamation"></i>`
    };
    return icons[type] || html`<i class="fa-sharp fa-light fa-circle"></i>`;
  }

  _renderHtml(str) {
    const el = document.createElement('span');
    el.innerHTML = str;
    return html`${Array.from(el.childNodes).map(node => {
      if (node.nodeType === Node.TEXT_NODE) return node.textContent;
      if (node.nodeName === 'STRONG') return html`<strong>${node.textContent}</strong>`;
      return node.textContent;
    })}`;
  }

  _formatDuration(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  _liveDuration(call) {
    // Compute duration from started_at if available (live WebSocket data)
    if (call.started_at) {
      const startMs = new Date(call.started_at.endsWith('Z') ? call.started_at : call.started_at + 'Z').getTime();
      const elapsed = Math.max(0, Math.floor((this._now - startMs) / 1000));
      return this._formatDuration(elapsed);
    }
    // Fallback: static duration from API
    if (call.duration != null) {
      return this._formatDuration(call.duration);
    }
    return '0:00';
  }

  _formatPhone(phone) {
    if (!phone) return 'Unknown';
    // US number: +1XXXXXXXXXX → (XXX) XXX-XXXX
    const usMatch = phone.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
    if (usMatch) return `(${usMatch[1]}) ${usMatch[2]}-${usMatch[3]}`;
    // International: +CC XXX XXX XXXX (group by 3s)
    if (phone.startsWith('+') && phone.length > 5) {
      const digits = phone.slice(1);
      const cc = digits.length > 10 ? digits.slice(0, digits.length - 10) : digits.slice(0, 2);
      const rest = digits.slice(cc.length);
      return `+${cc} ${rest.replace(/(\d{3})(?=\d)/g, '$1 ')}`;
    }
    return phone;
  }
}

customElements.define('modern-dashboard', ModernDashboard);
